<template>
  <div class="col-12 payment-methods-tab">
    <placeholder-content
      v-if="loading"
      :is-load="true"
    />
    <access-denied
      v-else-if="!hasPermission"
    />

    <template v-else>
      <!-- ===== EDITOR (create / edit form) ===== -->
      <div
        v-if="editor.open"
        class="card border-0 shadow-sm"
      >
        <div class="card-header bg-white d-flex align-items-center py-2">
          <button
            type="button"
            class="btn btn-sm btn-light border me-2"
            @click="closeEditor"
          >
            <i class="fa-solid fa-arrow-left me-1" />
            {{ $t('Back') }}
          </button>
          <i
            :class="[
              iconForMethod(form.method_name) || 'fa-solid fa-wallet',
              'me-2 text-primary'
            ]"
          />
          <b style="font-size: 15px;">
            {{
              editor.type === 'CREATE'
                ? $t('Add Payment Method')
                : $t('Edit Payment Method')
            }}
          </b>
          <span
            v-if="form.method_name"
            class="badge bg-info text-dark ms-2"
          >
            {{ $t(form.method_name) }}
          </span>
        </div>
        <div class="card-body" v-if="editor.loading">
          <placeholder-content :is-load="true" />
        </div>
        <div class="card-body" v-else-if="!editor.hasPermission">
          <access-denied />
        </div>
        <div class="card-body" v-else>
          <div class="row g-3">
            <div class="col-md-12">
              <label class="form-label">
                {{ $t('Payment Method') }}
                <span class="text-danger">*</span>
              </label>
              <select2
                v-model="form.payment_method_id"
                :options="methodOptions"
                :settings="{ ...select2Setting, disabled: editor.type === 'EDIT' }"
                @select="onSelectMethod"
              />
            </div>

            <!-- Dynamic fields per method type -->
            <div
              v-if="isBankTransfer"
              class="col-12"
            >
                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="pm_bank_name" class="form-label">
                      {{ $t('Bank Name') }}
                      <span class="text-danger">*</span>
                    </label>
                    <input
                      id="pm_bank_name"
                      type="text"
                      v-model.trim="v$.form.bank_name.$model"
                      class="form-control form-control-sm shadow-none"
                      :placeholder="$t('Enter bank name') + '...'"
                    >
                    <div
                      v-for="error of v$.form.bank_name.$errors"
                      class="d-block invalid-feedback"
                      :key="error.$uid"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label for="pm_account_no" class="form-label">
                      {{ $t('Account Number') }}
                      <span class="text-danger">*</span>
                    </label>
                    <input
                      id="pm_account_no"
                      type="text"
                      v-model.trim="v$.form.account_no.$model"
                      class="form-control form-control-sm shadow-none"
                      :placeholder="$t('Enter account number') + '...'"
                    >
                    <div
                      v-for="error of v$.form.account_no.$errors"
                      class="d-block invalid-feedback"
                      :key="error.$uid"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                  <div class="col-md-12">
                    <label for="pm_account_name" class="form-label">
                      {{ $t('Account Name') }}
                      <span class="text-danger">*</span>
                    </label>
                    <input
                      id="pm_account_name"
                      type="text"
                      v-model.trim="v$.form.account_name.$model"
                      class="form-control form-control-sm shadow-none"
                      :placeholder="$t('Enter account name') + '...'"
                    >
                    <div
                      v-for="error of v$.form.account_name.$errors"
                      class="d-block invalid-feedback"
                      :key="error.$uid"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else-if="isQRCode"
                class="col-12"
              >
                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="pm_promptpay_no" class="form-label">
                      {{ $t('PromptPay Number') }}
                      <span class="text-danger">*</span>
                    </label>
                    <input
                      id="pm_promptpay_no"
                      type="text"
                      v-model.trim="v$.form.promptpay_no.$model"
                      class="form-control form-control-sm shadow-none"
                      :placeholder="$t('Enter PromptPay number') + '...'"
                    >
                    <div
                      v-for="error of v$.form.promptpay_no.$errors"
                      class="d-block invalid-feedback"
                      :key="error.$uid"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label for="pm_account_name_qr" class="form-label">
                      {{ $t('Account Name') }}
                      <span class="text-danger">*</span>
                    </label>
                    <input
                      id="pm_account_name_qr"
                      type="text"
                      v-model.trim="v$.form.account_name.$model"
                      class="form-control form-control-sm shadow-none"
                      :placeholder="$t('Enter account name') + '...'"
                    >
                    <div
                      v-for="error of v$.form.account_name.$errors"
                      class="d-block invalid-feedback"
                      :key="error.$uid"
                    >
                      {{ error.$message }}
                    </div>
                  </div>
                </div>
              </div>
            <div
              v-else
              class="col-12 text-center text-muted py-3"
            >
              <i class="fa-regular fa-hand-pointer me-2" />
              {{ $t('Please select a payment method to continue.') }}
            </div>

            <div class="col-12">
              <label class="form-label" for="pm_active_switch">
                {{ $t('Status') }}
              </label>
              <div class="form-check form-switch">
                <input
                  class="form-check-input shadow-none"
                  type="checkbox"
                  id="pm_active_switch"
                  v-model="form.active"
                >
                <label class="form-check-label" for="pm_active_switch">
                  {{ form.active ? $t('Active') : $t('Inactive') }}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="!editor.loading && editor.hasPermission"
          class="card-footer bg-light d-flex justify-content-end py-2"
        >
          <button
            type="button"
            class="btn btn-sm btn-light border me-2"
            @click="closeEditor"
          >
            {{ $t('Cancel') }}
          </button>
          <ladda
            text="Save"
            :btn-class="'btn btn-primary btn-block btn-sm'"
            :loading="loadingSave"
            icon="<i class='fa-solid fa-floppy-disk'></i>"
            :disabled="v$.$invalid || !form.payment_method_id"
            @click="handleSave"
          />
        </div>
      </div>

      <!-- ===== LIST ===== -->
      <div
        v-else
        class="card border-0 shadow-sm"
      >
        <div class="card-header bg-white d-flex justify-content-between align-items-center py-2">
          <div class="d-flex align-items-center">
            <i class="fa-solid fa-credit-card me-2 text-primary" />
            <b style="font-size: 15px;">{{ $t('Payment Methods') }}</b>
            <span class="badge rounded-pill bg-secondary ms-2">
              {{ items.length }}
            </span>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="!filter.length"
            @click="openCreate"
          >
            <i class="fa-solid fa-circle-plus me-1" />
            {{ $t('Create') }}
          </button>
        </div>
        <div class="card-body p-0">
          <vue3-easy-data-table
            :headers="headers($t)"
            :items="items"
            :loading="tableLoading"
            :theme-color="'#515151'"
            :hide-footer="true"
            :hide-rows-per-page="true"
            :empty-message="$t('No Available Data')"
            :table-min-height="null"
            :body-item-class-name="bodyItemClassName"
            table-class-name="customize-table-in-offcanvas"
            header-text-direction="center"
          >
            <template #item-payment_method="item">
              <span
                :class="[
                  'badge',
                  isItemBankTransfer(item) ? 'bg-primary'
                  : isItemQRCode(item) ? 'bg-success'
                  : 'bg-info text-dark'
                ]"
              >
                <i :class="iconForMethod(item.payment_method) + ' me-1'" />
                {{ $t(item.payment_method) }}
              </span>
            </template>
            <template #item-account_name="item">
              <span v-if="item.account_name">
                {{ item.account_name }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #item-active="item">
              <div class="form-check form-switch d-inline-block m-0">
                <input
                  class="form-check-input shadow-none"
                  type="checkbox"
                  :checked="item.active"
                  :disabled="loadingToggleId === item.id"
                  @change="toggleActive(item)"
                >
              </div>
            </template>
            <template #item-created_at="item">
              <small class="text-muted">
                {{ formatDateTime(item.created_at) }}
              </small>
            </template>
            <template #item-action="item">
              <div class="icon-link custom-actions">
                <popper
                  :placement="'top'"
                  :hover="true"
                  :arrow="true"
                  :disableClickAway="true"
                  class="popper-default border-0 m-0"
                >
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-icon"
                    @click="openEdit(item)"
                  >
                    <i class="fa-solid fa-square-pen" />
                  </button>
                  <template #content>
                    <div>{{ $t('Edit') }}</div>
                  </template>
                </popper>
                <popper
                  :placement="'top'"
                  :hover="true"
                  :arrow="true"
                  :disableClickAway="true"
                  class="popper-default border-0 m-0"
                >
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-icon"
                    @click="handleDelete(item)"
                  >
                    <i class="fa-solid fa-trash-can" />
                  </button>
                  <template #content>
                    <div>{{ $t('Delete') }}</div>
                  </template>
                </popper>
              </div>
            </template>
            <template #loading>
              <i class="fa-solid fa-spinner fa-spin fa-2xl" />
            </template>
          </vue3-easy-data-table>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import Select2 from 'vue3-select2-component';
import Popper from 'vue3-popper';
import moment from 'moment';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-payment-method";
import headers from "./assets/template/datatable/PaymentMethodFieldsDef";
import { Branches } from '@/services/endpoint/branches.service';

const emptyForm = () => ({
  method_name: '',
  payment_method_id: null,
  bank_name: '',
  account_no: '',
  account_name: '',
  promptpay_no: '',
  active: true
});

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form);
  },
  props: {
    branchId: {
      type: Number,
      required: true
    }
  },
  components: {
    Vue3EasyDataTable,
    PlaceholderContent,
    AccessDenied,
    Ladda,
    Select2,
    Popper
  },
  data() {
    return {
      loading: true,
      tableLoading: false,
      hasPermission: false,
      loadingSave: false,
      loadingToggleId: null,
      items: [],
      filter: [],
      headers,
      select2Setting: {
        minimumResultsForSearch: Infinity,
        placeholder: this.$t('Select payment method') + '...'
      },
      editor: {
        open: false,
        type: 'CREATE',
        id: null,
        loading: false,
        hasPermission: true
      },
      form: emptyForm()
    }
  },
  computed: {
    methodOptions() {
      return this.filter.map(m => ({
        id: m.id,
        text: this.$t(m.name)
      }));
    },
    isBankTransfer() {
      return (this.form.method_name || '').toLowerCase().includes('bank');
    },
    isQRCode() {
      return (this.form.method_name || '').toLowerCase().includes('qr');
    }
  },
  async mounted() {
    this.loading = true;
    await this.fetchData();
    this.loading = false;
  },
  methods: {
    bodyItemClassName(column) {
      let className = 'align-middle py-1 px-2';
      if (['active'].includes(column)) className += ' text-center';
      return className;
    },
    formatDateTime(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },
    iconForMethod(name) {
      const n = (name || '').toLowerCase();
      if (n.includes('qr')) return 'fa-solid fa-qrcode';
      if (n.includes('bank')) return 'fa-solid fa-building-columns';
      if (n.includes('cash')) return 'fa-solid fa-money-bill-wave';
      if (n.includes('card') || n.includes('credit')) return 'fa-solid fa-credit-card';
      return 'fa-solid fa-wallet';
    },
    isItemBankTransfer(item) {
      return (item.payment_method || '').toLowerCase().includes('bank');
    },
    isItemQRCode(item) {
      return (item.payment_method || '').toLowerCase().includes('qr');
    },
    async fetchData() {
      // Use in-table spinner on refresh; the page-level placeholder only
      // runs once during mounted() for the very first load.
      this.tableLoading = true;
      try {
        const res = await Branches.getPaymentMethods(this.branchId);
        this.items = res.data.data || [];
        this.filter = res.data.filter || [];
        this.hasPermission = true;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        } else if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.tableLoading = false;
      }
    },
    onSelectMethod({ id }) {
      const m = this.filter.find(f => String(f.id) === String(id));
      this.form.payment_method_id = m ? m.id : null;
      this.form.method_name = m ? m.name : '';
      // Reset fields that don't belong to the newly chosen method type
      this.form.bank_name = '';
      this.form.account_no = '';
      this.form.promptpay_no = '';
      this.v$.$reset();
    },
    openCreate() {
      this.form = emptyForm();
      this.editor.type = 'CREATE';
      this.editor.id = null;
      this.editor.open = true;
      this.v$.$reset();
    },
    async openEdit(item) {
      // Open editor immediately in loading state, then fetch latest data
      // from GET /branches/{id}/payment-method/{methodId}/edit
      this.form = emptyForm();
      this.editor.type = 'EDIT';
      this.editor.id = item.id;
      this.editor.open = true;
      this.editor.loading = true;
      this.editor.hasPermission = true;
      this.v$.$reset();

      try {
        const res = await Branches.getUpdatePaymentMethod(this.branchId, item.id);
        const d = res.data.data || {};
        const match = this.filter.find(
          f => (f.name || '').toLowerCase() === (d.payment_method || '').toLowerCase()
        );
        this.form = {
          method_name: d.payment_method || '',
          payment_method_id: d.payment_method_id || (match ? match.id : null),
          bank_name: d.bank_name || '',
          account_no: d.account_no || '',
          account_name: d.account_name || '',
          promptpay_no: d.promptpay_no || '',
          active: !!d.is_active
        };
      } catch (err) {
        if (err?.response?.status === 403) {
          this.editor.hasPermission = false;
        } else {
          if (err?.response?.data?.message) {
            this.$toast.warn(this.$t(err?.response?.data?.message));
          }
          this.editor.open = false;
        }
      } finally {
        this.editor.loading = false;
        this.v$.$reset();
      }
    },
    closeEditor() {
      this.editor.open = false;
      this.editor.id = null;
      this.editor.loading = false;
      this.editor.hasPermission = true;
      this.form = emptyForm();
      this.v$.$reset();
    },
    setParamsBeforeSend() {
      // Spec: only send allowed keys; never send empty string (omit or null);
      // is_active must be a real boolean.
      const params = {
        payment_method_id: this.form.payment_method_id,
        account_name: this.form.account_name,
        is_active: this.form.active === true
      };
      if (this.isBankTransfer) {
        if (this.form.bank_name) params.bank_name = this.form.bank_name;
        if (this.form.account_no) params.account_no = this.form.account_no;
      } else if (this.isQRCode) {
        if (this.form.promptpay_no) params.promptpay_no = this.form.promptpay_no;
      }
      return params;
    },
    async handleSave() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.form.payment_method_id) return;

      this.loadingSave = true;
      const params = this.setParamsBeforeSend();
      const isCreate = this.editor.type === 'CREATE';
      const call = isCreate
        ? Branches.createPaymentMethod(this.branchId, params)
        : Branches.updatePaymentMethod(this.branchId, this.editor.id, params);

      try {
        await call;
        this.$toast.success(
          "<B>" + this.$t('Success') + "</B>\n" +
          this.$t(isCreate ? 'Save Success!' : 'Update Success!')
        );
        this.closeEditor();
        await this.fetchData();
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loadingSave = false;
      }
    },
    async toggleActive(item) {
      const previous = !!item.active;
      this.loadingToggleId = item.id;
      try {
        await Branches.togglePaymentMethodActive(this.branchId, item.id, !previous);
        // Re-fetch: activating one record auto-deactivates siblings with the
        // same payment_method_id in this branch.
        await this.fetchData();
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loadingToggleId = null;
      }
    },
    async handleDelete(item) {
      const self = this;
      this.$swal({
        icon: "warning",
        iconColor: "#FF7043",
        title: this.$t("Delete this Payment Method") + '?',
        html: this.$t("You will not be able to undo this action."),
        showCancelButton: true,
        cancelButtonText: this.$t("Cancel"),
        confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm"),
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-primary me-2",
          cancelButton: "btn btn-danger",
        },
        focusConfirm: false,
        showLoaderOnConfirm: true,
        preConfirm: async function () {
          return Branches.deletePaymentMethod(self.branchId, item.id)
            .then(() => {
              self.$toast.success(
                "<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!')
              );
              self.fetchData();
            })
            .catch((error) => {
              const code = error?.response?.data?.code;
              if (code === "VALIDATION" || code === "VALIDATION_ERROR") {
                self.$swal.showValidationMessage(
                  self.$t(error?.response?.data?.message)
                );
              } else if (error?.response?.data?.message) {
                self.$toast.warn(error.response.data.message);
              }
            });
        },
      });
    }
  }
}
</script>

<style scoped>
.payment-methods-tab :deep(.customize-table-in-offcanvas) {
  --easy-table-border: 1px solid #eaeaea;
  --easy-table-row-border: 1px solid #f1f1f1;
  --easy-table-header-font-size: 12px;
  --easy-table-header-height: 38px;
  --easy-table-header-font-color: #515151;
  --easy-table-header-background-color: #f8f9fa;
  --easy-table-body-row-font-size: 13px;
  --easy-table-body-row-height: 44px;
  --easy-table-body-row-hover-background-color: #f5f8ff;
  --easy-table-body-item-padding: 8px 12px;
  border: 0;
}

.payment-methods-tab .card {
  border-radius: 10px;
  overflow: hidden;
}

.payment-methods-tab .card-header {
  border-bottom: 1px solid #ececec;
}

/* focus ของ input ใช้ธีมกลางจาก custom.scss (ขอบส้ม ไม่มีเงา) — เดิมที่นี่
   ฮาร์ดโค้ดขอบฟ้าของ Bootstrap ทับไว้ ทำให้หน้านี้หลุดธีมอยู่หน้าเดียว */

.payment-methods-tab .form-switch .form-check-input {
  cursor: pointer;
}
</style>
